import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, Truck, RotateCcw, ShieldCheck, Minus, Plus, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { ProductCard } from "@/components/store/ProductCard";
import { formatPrice } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t } = useLanguage();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const [zoom, setZoom] = useState({ active: false, x: 50, y: 50 });
  const zoomRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data.product);
      setRelated(r.data.related);
      setActiveImg(0);
      setSize(r.data.product.sizes?.[0] || null);
      setColor(r.data.product.colors?.[0] || null);
      setQty(1);
    }).catch(() => setProduct(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;
  }
  if (!product) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-center px-5">
        <div>
          <h1 className="font-serif text-4xl mb-4">{t("Piece not found")}</h1>
          <Link to="/shop"><Button className="rounded-none">{t("Back to shop")}</Button></Link>
        </div>
      </div>
    );
  }

  const onSale = !!product.sale_price;
  const soldOut = product.stock <= 0;
  const price = product.sale_price ?? product.price;

  const buildItem = () => ({ quantity: qty, size, color });

  const handleAdd = () => {
    addItem(product, buildItem());
    toast.success(`${product.name} ${t("added to bag")}`);
  };

  const onMove = (e) => {
    const rect = zoomRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoom({ active: true, x, y });
  };

  const specs = [
    ["Material", product.material],
    ["Weight", product.weight],
    ["Dimensions", product.dimensions],
    ["SKU", product.sku],
    ["Collection", product.collection],
    ["Available Finishes", product.colors?.join(", ")],
  ].filter(([, v]) => v);

  return (
    <div data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 pt-8">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Link to="/" className="hover:text-foreground">{t("Home")}</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to={`/category/${product.category}`} className="hover:text-foreground capitalize">{product.category}</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground truncate">{product.name}</span>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-10 grid lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Gallery */}
        <div className="flex flex-col-reverse sm:flex-row gap-4">
          <div className="flex sm:flex-col gap-3">
            {product.images?.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                className={`w-20 h-24 shrink-0 overflow-hidden bg-secondary border transition-colors ${activeImg === i ? "border-gold" : "border-transparent hover:border-border"}`}
                data-testid={`thumbnail-${i}`}
              >
                <img src={img} alt={`${product.name} ${i + 1}`} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
          <div
            ref={zoomRef}
            className="relative flex-1 aspect-[4/5] overflow-hidden bg-secondary cursor-zoom-in"
            onMouseEnter={() => setZoom((z) => ({ ...z, active: true }))}
            onMouseLeave={() => setZoom({ active: false, x: 50, y: 50 })}
            onMouseMove={onMove}
            data-testid="main-image"
          >
            <img
              src={product.images?.[activeImg]}
              alt={product.name}
              className="h-full w-full object-cover transition-transform duration-200"
              style={{
                transform: zoom.active ? "scale(1.9)" : "scale(1)",
                transformOrigin: `${zoom.x}% ${zoom.y}%`,
              }}
            />
            {onSale && (
              <span className="absolute top-4 left-4 bg-gold text-gold-foreground text-[10px] font-semibold uppercase tracking-widest px-3 py-1.5">
                Sale
              </span>
            )}
          </div>
        </div>

        {/* Info */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="overline text-gold mb-3">{product.collection} {t("Collection")}</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light leading-tight" data-testid="detail-product-name">{product.name}</h1>

          <div className="mt-6 flex items-center gap-4">
            {onSale ? (
              <>
                <span className="text-3xl font-light text-gold" data-testid="detail-price">{formatPrice(product.sale_price)}</span>
                <span className="text-xl text-muted-foreground line-through">{formatPrice(product.price)}</span>
              </>
            ) : (
              <span className="text-3xl font-light" data-testid="detail-price">{formatPrice(product.price)}</span>
            )}
          </div>

          <p className="mt-6 text-muted-foreground leading-relaxed">{product.description}</p>

          {/* Stock */}
          <div className="mt-6 text-sm" data-testid="stock-status">
            {soldOut ? (
              <span className="text-destructive">{t("Currently sold out")}</span>
            ) : product.stock <= 8 ? (
              <span className="text-amber-500">{t("Only")} {product.stock} {t("left — order soon")}</span>
            ) : (
              <span className="text-emerald-500 inline-flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-emerald-500" /> {t("In stock")}</span>
            )}
          </div>

          {/* Sizes */}
          {product.sizes?.length > 0 && (
            <div className="mt-8">
              <p className="overline mb-3">{t("Size")}</p>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    className={`min-w-12 h-11 px-4 text-sm border transition-colors ${size === s ? "border-gold text-gold" : "border-border hover:border-foreground"}`}
                    data-testid={`size-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Colors */}
          {product.colors?.length > 0 && (
            <div className="mt-6">
              <p className="overline mb-3">{t("Finish")}</p>
              <div className="flex flex-wrap gap-2">
                {product.colors.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    className={`h-11 px-4 text-sm border transition-colors ${color === c ? "border-gold text-gold" : "border-border hover:border-foreground"}`}
                    data-testid={`color-${c}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quantity + actions */}
          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-border">
              <button className="h-12 w-12 grid place-items-center hover:bg-secondary transition-colors" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-decrease"><Minus className="h-4 w-4" /></button>
              <span className="w-10 text-center" data-testid="qty-value">{qty}</span>
              <button className="h-12 w-12 grid place-items-center hover:bg-secondary transition-colors" onClick={() => setQty((q) => Math.min(product.stock || 99, q + 1))} data-testid="qty-increase"><Plus className="h-4 w-4" /></button>
            </div>
          </div>

          <div className="mt-4">
            <Button className="w-full rounded-none h-14" onClick={handleAdd} disabled={soldOut} data-testid="add-to-cart-btn">
              <ShoppingBag className="h-4 w-4 mr-2" /> {t("Add to Bag")}
            </Button>
          </div>

          {/* Reassurance */}
          <div className="mt-8 grid grid-cols-3 gap-4 text-center py-6 border-y border-border">
            <div className="flex flex-col items-center gap-2"><Truck className="h-5 w-5 text-gold" strokeWidth={1.3} /><span className="text-xs text-muted-foreground">{t("Free Shipping")}</span></div>
            <div className="flex flex-col items-center gap-2"><RotateCcw className="h-5 w-5 text-gold" strokeWidth={1.3} /><span className="text-xs text-muted-foreground">{t("30-Day Returns")}</span></div>
            <div className="flex flex-col items-center gap-2"><ShieldCheck className="h-5 w-5 text-gold" strokeWidth={1.3} /><span className="text-xs text-muted-foreground">{t("Lifetime Warranty")}</span></div>
          </div>

          {/* Accordions */}
          <Accordion type="single" collapsible defaultValue="specs" className="mt-6">
            <AccordionItem value="specs">
              <AccordionTrigger className="overline" data-testid="accordion-specs">{t("Specifications")}</AccordionTrigger>
              <AccordionContent>
                <table className="w-full text-sm">
                  <tbody>
                    {specs.map(([k, v]) => (
                      <tr key={k} className="border-b border-border last:border-0">
                        <td className="py-2.5 text-muted-foreground w-1/3">{t(k)}</td>
                        <td className="py-2.5">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="shipping">
              <AccordionTrigger className="overline" data-testid="accordion-shipping">{t("Shipping & Delivery")}</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                {t("Complimentary insured shipping within the United States on every order. Standard delivery in 3–5 business days, with express options available. Every piece arrives in signature M&A Jewelry packaging with a certificate of authenticity.")}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="returns">
              <AccordionTrigger className="overline" data-testid="accordion-returns">{t("Returns & Warranty")}</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                {t("Enjoy 30-day complimentary returns on unworn items in original condition. All M&A Jewelry pieces are covered by a lifetime warranty against manufacturing defects and includes free annual cleaning and inspection.")}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </motion.div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16">
          <h2 className="font-serif text-4xl font-light mb-10">{t("You may also love")}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 lg:gap-8">
            {related.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
